/**
 * CostSummaryByTier — parts cost totals for DIY items, grouped by priority tier.
 * Contractor items are excluded since labour costs vary too much by region.
 */

import { Card, CardContent } from '@/shared/components/ui/card';
import type { ActionItem, Priority } from '../types/homeAudit.types';

type CostSummaryByTierProps = {
  actionItems: ActionItem[];
};

const TIERS: Priority[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const LABELS: Record<Priority, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

export const CostSummaryByTier = ({ actionItems }: CostSummaryByTierProps) => {
  const rows = TIERS.map((tier) => {
    const items = actionItems.filter((i) => i.priority === tier && i.isDiy && i.costLow > 0);
    if (items.length === 0) return null;
    const low = items.reduce((sum, i) => sum + i.costLow, 0);
    const high = items.reduce((sum, i) => sum + i.costHigh, 0);
    return { tier, count: items.length, low, high };
  }).filter((r): r is { tier: Priority; count: number; low: number; high: number } => r !== null);

  if (rows.length === 0) return null;

  return (
    <Card className="p-6" data-testid="cost-summary">
      <CardContent className="p-0 space-y-3">
        <h2 className="text-lg font-medium">Estimated Parts Cost</h2>
        <p className="text-sm text-muted-foreground">DIY items only — contractor labour not included as costs vary by region.</p>
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.tier} className="flex items-center justify-between text-sm">
              <span>
                <span className="font-medium">{LABELS[row.tier]}</span>
                <span className="text-muted-foreground"> · {row.count} item{row.count === 1 ? '' : 's'}</span>
              </span>
              <span className="font-medium">~${row.low}–${row.high}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
